<script setup lang="ts">
import { computed, ref } from "vue";

import SkoreSpinner from "@/components/ui/SkoreSpinner.vue";
import FullPageCenteredLayout from "@/layouts/FullPageCenteredLayout.vue";
import { RouteNames, router } from "@/router";
import { useApi } from "@/services/api";
import OnboardingViewCreateWorkspaceStep from "@/views/onboarding/OnboardingViewCreateWorkspaceStep.vue";
import OnboardingViewInviteStep from "@/views/onboarding/OnboardingViewInviteStep.vue";
import OnboardingViewPutReportStep from "@/views/onboarding/OnboardingViewPutReportStep.vue";

const api = useApi();

const steps = [
  {
    title: "Welcome to Skore",
    subtitle: "Set-up your workspace",
    component: OnboardingViewCreateWorkspaceStep,
  },
  {
    title: "Add your team members",
    subtitle: "",
    component: OnboardingViewInviteStep,
  },
  {
    title: "Get started using a local IDE or notebook",
    subtitle: "Push and pull Skore reports directly from your Python environment to Skore Hub.",
    component: OnboardingViewPutReportStep,
  },
];

const currentStepIndex = ref(0);
const workspaceId = ref<number | null>(null);
const workspacePublicId = ref<string | null>(null);

const currentStep = computed(() => steps[currentStepIndex.value]!);

async function onNextStep(stepData?: { workspaceId?: number; workspacePublicId?: string }) {
  if (stepData?.workspaceId) {
    workspaceId.value = stepData.workspaceId;

    const workspace = await api.fetchWorkspace(workspaceId.value);
    if (workspace) {
      workspacePublicId.value = workspace.public_id;
    }
  }
  nextStep();
}

function onSkipStep() {
  nextStep();
}

async function nextStep() {
  if (currentStepIndex.value === steps.length - 1 && workspaceId.value) {
    const workspace = await api.fetchWorkspace(workspaceId.value);
    if (workspace) {
      router.replace({
        name: RouteNames.Projects,
        params: { workspacePublicId: workspace.public_id },
      });
    }
  } else {
    currentStepIndex.value++;
  }
}
</script>

<template>
  <FullPageCenteredLayout :show-logo="false">
    <div v-if="currentStepIndex === -1" class="loading">
      <SkoreSpinner />
    </div>
    <div v-else class="onboarding">
      <header>
        <img src="@/assets/images/skore-text.svg" alt="skore" />
        <h1 class="text-3xl-semibold">{{ currentStep.title }}</h1>
        <h2 v-if="currentStep.subtitle.length > 0" class="text-base-regular">
          {{ currentStep.subtitle }}
        </h2>
      </header>

      <div class="step">
        <component
          :is="currentStep.component"
          :workspace-id="workspaceId"
          :workspace-name="workspacePublicId"
          @continue="onNextStep"
          @skip="onSkipStep"
          class="step-component"
        />
      </div>
    </div>
  </FullPageCenteredLayout>
</template>

<style scoped>
.onboarding {
  display: flex;
  width: 100dvw;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding-bottom: var(--numbers-32);
  gap: var(--numbers-32);

  header {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: var(--numbers-8);
    text-align: center;

    img {
      width: auto;
      height: var(--numbers-24);
    }

    h2 {
      color: var(--content-tertiary);
    }
  }

  .step {
    width: 60dvw;

    .step-component {
      width: 100%;
    }
  }
}

@media screen and (width >=801px) {
  .onboarding {
    .step {
      width: 70dvw;
    }
  }
}

@media screen and (width >=1025px) {
  .onboarding {
    .step {
      width: 60dvw;
    }
  }
}

@media screen and (width >=1279px) {
  .onboarding {
    .step {
      width: 55dvw;
    }
  }
}
</style>
